import { useState, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { map, get } from 'lodash'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormikTextField from '../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../shared/components/formik/FormikSelectField'
import FormikNumericField from '../../../shared/components/formik/FormikNumericField'
import useHetznerServersResources from '../../../shared/resources/api/v1/cloud/useHetznerServersResources'
import useHetznerProjectsResources from '../../../shared/resources/api/v1/cloud/useHetznerProjectsResources'
import { useTeam } from '../../../contexts/TeamContext'
import { useHetznerProjects } from '../../../contexts/HetznerProjectsContext'
import { useHetznerServers } from '../../../contexts/HetznerServersContext'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'

function HetznerServerCreatePage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { teamSlug } = useParams()
  const [searchParams] = useSearchParams()
  const { team } = useTeam()
  const { projects, hetznerProjectsById, loading: projectsLoading } = useHetznerProjects()
  const { reload: reloadServers } = useHetznerServers()

  const projectIdFromUrl = searchParams.get('projectId')
  const [selectedProjectId, setSelectedProjectId] = useState(projectIdFromUrl || '')
  const [metadata, setMetadata] = useState(null)
  const [metadataLoading, setMetadataLoading] = useState(false)
  const [metadataError, setMetadataError] = useState(null)

  const { create } = useHetznerServersResources(teamSlug, selectedProjectId)
  const { metadata: fetchMetadata } = useHetznerProjectsResources(teamSlug)

  // Fetch metadata when project is selected
  useEffect(() => {
    if (!selectedProjectId) {
      setMetadata(null)
      return
    }

    setMetadataLoading(true)
    setMetadataError(null)

    fetchMetadata(selectedProjectId)
      .then((response) => {
        setMetadata(response.metadata)
      })
      .catch((err) => {
        setMetadataError(err.message || 'Failed to load metadata')
      })
      .finally(() => {
        setMetadataLoading(false)
      })
  }, [selectedProjectId])

  const projectOptions = useMemo(() => {
    return map(projects, (project) => ({
      value: project.id,
      label: project.name,
    }))
  }, [projects])

  const datacenterOptions = useMemo(() => {
    if (!metadata?.datacenters) return []
    return map(metadata.datacenters, (dc) => ({
      value: dc.name,
      label: `${dc.name} - ${dc.description}`,
    }))
  }, [metadata])

  const validationSchema = Yup.object({
    projectId: projectIdFromUrl
      ? Yup.string()
      : Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    architecture: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    serverType: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    datacenter: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    image: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    volumeSize: Yup.number().min(10).required(intl.formatMessage({ id: 'login.error.required' })),
  })

  const formik = useFormik({
    initialValues: {
      projectId: projectIdFromUrl || '',
      name: '',
      architecture: 'x86',
      serverType: '',
      datacenter: '',
      image: '',
      volumeSize: 50,
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const serverData = {
        server: {
          name: values.name,
          serverType: values.serverType,
          datacenter: values.datacenter,
          image: values.image,
          volumes: [
            {
              size: values.volumeSize,
              mountAt: '/mnt/docker',
              format: 'ext4',
            },
          ],
        },
      }

      create(serverData)
        .then(() => reloadServers())
        .then(() => {
          navigate(`/teams/${team.slug}/cloud/hetzner`)
        })
        .catch((err) => {
          const apiError = get(err, 'data.error')
          setStatus(apiError || err.message || 'Error creating server')
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  })

  const imageOptions = useMemo(() => {
    if (!metadata?.images) return []
    const filteredImages = metadata.images.filter(image => image.architecture === formik.values.architecture)
    return map(filteredImages, (image) => ({
      value: image.name,
      label: image.description || image.name,
    }))
  }, [metadata, formik.values.architecture])

  const architectureOptions = useMemo(() => [
    { value: 'x86', label: 'x86 (amd64)' },
    { value: 'arm', label: 'ARM (arm64)' },
  ], [])

  const serverTypeOptions = useMemo(() => {
    if (!metadata?.datacenters || !formik.values.datacenter) return []

    const selectedDc = metadata.datacenters.find(dc => dc.name === formik.values.datacenter)
    if (!selectedDc?.serverTypes) return []

    const filteredTypes = selectedDc.serverTypes.filter(type => type.architecture === formik.values.architecture)
    const sortedTypes = [...filteredTypes].sort((a, b) => {
      if (a.cpuCores !== b.cpuCores) return a.cpuCores - b.cpuCores
      return a.memory - b.memory
    })

    return map(sortedTypes, (type) => ({
      value: type.name,
      label: `${type.name} (${type.cpuCores} vCPU, ${type.memory}GB RAM, ${type.disk}GB disk) - ${intl.formatNumber(Number(type.priceMonthly), { style: 'currency', currency: 'EUR' })}/mo`,
    }))
  }, [metadata, formik.values.datacenter, formik.values.architecture, intl])

  // Update selectedProjectId when form value changes
  useEffect(() => {
    if (formik.values.projectId && formik.values.projectId !== selectedProjectId) {
      setSelectedProjectId(formik.values.projectId)
      // Reset dependent fields when project changes
      formik.setFieldValue('datacenter', '')
      formik.setFieldValue('serverType', '')
      formik.setFieldValue('image', '')
    }
  }, [formik.values.projectId])

  // Reset serverType when datacenter changes
  const prevDatacenterRef = useRef(formik.values.datacenter)
  useEffect(() => {
    if (prevDatacenterRef.current && prevDatacenterRef.current !== formik.values.datacenter) {
      formik.setFieldValue('serverType', '')
    }
    prevDatacenterRef.current = formik.values.datacenter
  }, [formik.values.datacenter])

  // Reset serverType and image when architecture changes
  const prevArchitectureRef = useRef(formik.values.architecture)
  useEffect(() => {
    if (prevArchitectureRef.current && prevArchitectureRef.current !== formik.values.architecture) {
      formik.setFieldValue('serverType', '')
      formik.setFieldValue('image', '')
    }
    prevArchitectureRef.current = formik.values.architecture
  }, [formik.values.architecture])

  if (projectsLoading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team }} />
        <Typography variant="h4" component="h1" gutterBottom>
          <FormattedMessage id="hetzner.server.create.title" />
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          {!projectIdFromUrl && (
            <Box sx={{ mb: 2 }}>
              <FormikSelectField
                formik={formik}
                name="projectId"
                label={<FormattedMessage id="hetzner.server.create.projectLabel" />}
                options={projectOptions}
              />
            </Box>
          )}

          {selectedProjectId && metadataLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {selectedProjectId && metadataError && (
            <Typography color="error" sx={{ my: 2 }}>
              {metadataError}
            </Typography>
          )}

          {selectedProjectId && metadata && (
            <>
              <FormikTextField
                formik={formik}
                name="name"
                label={<FormattedMessage id="hetzner.server.create.nameLabel" />}
                autoFocus
              />

              <Box sx={{ mt: 2 }}>
                <FormikSelectField
                  formik={formik}
                  name="architecture"
                  label={<FormattedMessage id="hetzner.server.create.architectureLabel" />}
                  options={architectureOptions}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormikSelectField
                  formik={formik}
                  name="datacenter"
                  label={<FormattedMessage id="hetzner.server.create.datacenterLabel" />}
                  options={datacenterOptions}
                />
              </Box>

              {formik.values.datacenter && (
                <Box sx={{ mt: 2 }}>
                  <FormikSelectField
                    formik={formik}
                    name="serverType"
                    label={<FormattedMessage id="hetzner.server.create.serverTypeLabel" />}
                    options={serverTypeOptions}
                  />
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <FormikSelectField
                  formik={formik}
                  name="image"
                  label={<FormattedMessage id="hetzner.server.create.imageLabel" />}
                  options={imageOptions}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormikNumericField
                  formik={formik}
                  name="volumeSize"
                  label={<FormattedMessage id="hetzner.server.create.volumeSizeLabel" />}
                  helperText={<FormattedMessage id="hetzner.server.create.volumeSizeHint" />}
                />
              </Box>
            </>
          )}

          {formik.status && (
            <Typography color="error" sx={{ mt: 2 }}>
              {formik.status}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting || !selectedProjectId || !metadata}
            sx={{ mt: 3 }}
          >
            <FormattedMessage id="hetzner.server.create.submitButton" />
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default HetznerServerCreatePage
